import { ThemeOptions } from '@mui/material/styles/createTheme';

import { DataPopperProps, DataPopperRowProps } from './types';
import { mapPaletteColorToDataColorToken } from '../../utils/mapPaletteColorToDataColorToken';
import { tokens } from '../../theme/generated/tokens';
import { getThemeColor } from '../../utils/theme';

export const dataPopperTheme: ThemeOptions['components'] = {
  DsDataPopper: {
    styleOverrides: {
      root: () => ({
        backgroundColor: tokens.theme.bg.structural.surface,
        borderRadius: tokens.theme.radius.md,
        boxShadow: tokens.theme.shadows.elevation.medium,
        padding: '4px 0',
        display: 'flex',
        flexDirection: 'column',
      }),
    },
  },

  DsDataPopperRow: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const size = ownerState?.size || ownerState.popperProps?.size;
        let paddingX = 20;
        let gap = 8;
        let height = 32;
        if (size === 'small') {
          paddingX = 12;
          gap = 4;
          height = 28;
        } else if (size === 'large') {
          height = 36;
        }
        let typography = theme.typography.P12R;
        if (size === 'small') {
          typography = theme.typography.P10R;
        } else if (size === 'large') {
          typography = theme.typography.P14R;
        }
        return {
          display: 'flex',
          alignItems: 'center',
          gap,
          height,
          paddingLeft: paddingX,
          paddingRight: paddingX,
          color: tokens.theme.fg.structural.primary,
          ...typography,
        };
      },
      rowContent: ({ ownerState }) => {
        const hasLeft = ownerState?.left !== undefined;
        const hasRight = ownerState?.right !== undefined;
        let justifyContent = 'space-between';

        if (hasLeft && !hasRight) {
          justifyContent = 'flex-start';
        } else if (hasRight && !hasLeft) {
          justifyContent = 'flex-end';
        }
        return {
          display: 'flex',
          flexGrow: 1,
          minWidth: 0,
          columnGap: 4,
          alignItems: 'center',
          justifyContent,
        };
      },
    },
  },

  DsDataPopperRowIndicator: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const rowProps = ownerState?.rowProps as DataPopperRowProps;
        const popperProps = ownerState?.popperProps as DataPopperProps;
        const size =
          (ownerState?.size || rowProps?.size || popperProps?.size) === 'small'
            ? 8
            : 12;

        const indicatorColor = ownerState?.color;
        const semanticColor = mapPaletteColorToDataColorToken(
          indicatorColor,
          indicatorColor
        );
        const themeColor = getThemeColor(theme, semanticColor);

        let background = 'none';
        let border = 'none';
        let borderStyle = 'none';

        switch (ownerState.variant) {
          case 'filled':
            background = themeColor;
            break;
          case 'dashed':
            border = `1px solid ${themeColor}`;
            borderStyle = 'dashed';
            break;
          case 'outlined':
            border = `1px solid ${themeColor}`;
            borderStyle = 'solid';
            break;
          case 'hatched':
            background = `repeating-linear-gradient(
            -45deg,
            ${themeColor},
            ${themeColor} 1px,
            transparent 0px,
            transparent 2px
          )`;
        }

        const variantStyles = {
          background,
          border,
          borderStyle,
        };

        return {
          boxSizing: 'border-box',
          flexShrink: 0,
          width: size,
          height: size,
          borderRadius: tokens.theme.radius.xs,
          ...variantStyles,
        };
      },
    },
  },

  DsDataPopperRowDivider: {
    styleOverrides: {
      root: () => ({
        borderColor: tokens.theme.border.structural.neutral,
        '&:first-child, &:last-child /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */':
          {
            display: 'none',
          },
      }),
    },
  },

  DsDataPopperCell: {
    styleOverrides: {
      root: {
        display: 'flex',
        minWidth: 0,
      },
    },
  },
};
